import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useSubscription } from '@trpc/tanstack-react-query'
import { useTRPC } from './trpc'
import { useAuth } from './auth'

/** the kinds of record the server's feed reports; each names the lists to read again */
export type ActivityKind = 'swap' | 'position' | 'strategy' | 'deposit' | 'withdrawal' | 'transfer' | 'privateWithdrawal' | 'privateDeposit' | 'notification' | 'kraken'
const ALL_KINDS: ActivityKind[] = ['swap', 'position', 'strategy', 'deposit', 'withdrawal', 'transfer', 'privateWithdrawal', 'privateDeposit', 'notification', 'kraken']

type Trpc = ReturnType<typeof useTRPC>
/** every query that shows a kind: the in-progress bar, the history, an open dialog's status */
function keysOf(trpc: Trpc, kind: ActivityKind) {
  switch (kind) {
    case 'swap':
      // a position's swaps show inside the position; the P&L page's figures are these trades too (John, Nov 4 2026)
      return [trpc.swap.list.queryKey(), trpc.swap.status.queryKey(), trpc.swap.detail.queryKey(), trpc.position.get.queryKey(), trpc.swap.pnlTrades.queryKey()]
    case 'position':
      return [trpc.position.list.queryKey(), trpc.position.get.queryKey()]
    case 'strategy':
      return [trpc.strategy.list.queryKey(), trpc.strategy.get.queryKey(), trpc.swap.pnlTrades.queryKey()]
    case 'deposit':
      return [trpc.vault.deposits.queryKey(), trpc.vault.deposit.queryKey(), trpc.deposit.list.queryKey()]
    case 'withdrawal':
      // a withdrawal to Kraken that lands should show on the Kraken card at once (John, Nov 2 2026)
      return [trpc.vault.withdrawals.queryKey(), trpc.vault.withdrawal.queryKey(), trpc.kraken.balances.queryKey()]
    case 'transfer':
      return [trpc.transfer.list.queryKey(), trpc.transfer.status.queryKey(), trpc.kraken.balances.queryKey()]
    case 'privateWithdrawal':
      return [trpc.privateWithdrawal.list.queryKey(), trpc.privateWithdrawal.get.queryKey()]
    case 'privateDeposit':
      return [trpc.privateDeposit.list.queryKey(), trpc.privateDeposit.get.queryKey()]
    case 'notification':
      return [trpc.notification.list.queryKey()]
    case 'kraken':
      // a sale on Kraken: the history row and the Kraken card's balances
      return [trpc.kraken.orders.queryKey(), trpc.kraken.withdrawals.queryKey(), trpc.kraken.balances.queryKey()]
  }
}

/**
 * Reads the lists of these kinds again, right now: called when a flow is
 * started from a dialog (John, Oct 31 2026: the in-progress bar shows a swap
 * the moment it starts, not on the next poll) and for every event the feed
 * delivers.
 */
export function useRefreshActivity() {
  const trpc = useTRPC()
  const queryClient = useQueryClient()
  return useCallback(
    (kinds: ActivityKind[] = ALL_KINDS) => {
      for (const kind of kinds) {
        for (const queryKey of keysOf(trpc, kind)) void queryClient.invalidateQueries({ queryKey })
      }
      // the in-progress bar has its own read of what is moving; any change may start or end a flow
      void queryClient.invalidateQueries({ queryKey: trpc.progress.list.queryKey() })
    },
    [trpc, queryClient],
  )
}

/**
 * The server's activity feed (John, Oct 31 2026: no delays): one connection
 * per signed-in tab, and every event reads the lists it names again, so a
 * swap shows in the in-progress bar as it starts and its toast fires as it
 * lands. A dropped connection comes back on its own; a refused one is
 * retried with a fresh ticket, more slowly each time. Polling stays
 * underneath as the safety net.
 */
export function ActivityFeedProvider({ children }: { children: ReactNode }) {
  const { token } = useAuth()
  const [attempt, setAttempt] = useState(0)
  const failures = useRef(0)
  const onLive = useCallback(() => {
    failures.current = 0
  }, [])
  const onFailed = useCallback(() => {
    const delay = Math.min(60_000, 3_000 * 2 ** failures.current)
    failures.current += 1
    window.setTimeout(() => setAttempt((n) => n + 1), delay)
  }, [])
  return (
    <>
      {token ? <Feed key={`${token.slice(-12)}:${attempt}`} onLive={onLive} onFailed={onFailed} /> : null}
      {children}
    </>
  )
}

function Feed({ onLive, onFailed }: { onLive: () => void; onFailed: () => void }) {
  const trpc = useTRPC()
  const refresh = useRefreshActivity()
  const pending = useRef(new Set<ActivityKind>())
  const timer = useRef<number | null>(null)
  const wasLive = useRef(false)
  useSubscription(
    trpc.activity.changes.subscriptionOptions(undefined, {
      onData: (event) => {
        // several records change together (a fill touches the swap, the position and the strategy): one read, a beat later
        pending.current.add(event.kind)
        if (timer.current !== null) return
        timer.current = window.setTimeout(() => {
          timer.current = null
          const kinds = [...pending.current]
          pending.current.clear()
          refresh(kinds)
        }, 100)
      },
      onConnectionStateChange: (state) => {
        if (state.state !== 'pending') return
        onLive()
        // back after a gap: whatever happened meanwhile is read now
        if (wasLive.current) refresh()
        wasLive.current = true
      },
      onError: onFailed,
    }),
  )
  useEffect(
    () => () => {
      if (timer.current !== null) window.clearTimeout(timer.current)
    },
    [],
  )
  return null
}
